import React, { useRef, useState, useEffect } from 'react'
import { withStyles, Typography, CircularProgress } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import {
  aICreatorUploadAttachmentAction,
  aICreatorCheckAttachmentStatusAction,
  aICreatorGetAttachmentTitlesAction,
} from 'src/reducers/ai-creator/action'
import styles from './styles'
import {
  AttachmentUploader,
  ContentSelector,
  ResourceBanner,
} from './components'
import UserType from 'src/model/user-types'
const PdfView = ({
  classes,
  onChange,
  isPage,
  initialFlattenedHeaders,
  onFlattenedHeadersChange,
}) => {
  const dispatch = useDispatch()
  const attachments = useSelector(state => state.aICreator.attachments)
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [selectedTitle, setSelectedTitle] = useState(null)
  const [flattenedHeaders, setFlattenedHeaders] = useState(
    initialFlattenedHeaders || [],
  )
  const fileInputRef = useRef(null)
  // Track which attachment IDs we last fetched titles for. When the set
  // changes (e.g. the parent re-fetches course attachments and the source
  // file is now different), we refetch instead of reusing stale cache.
  const lastFetchedIdsRef = useRef('')

  const handleUploadFile = file => {
    const allowedExtensions = ['pdf', 'docx', 'doc']
    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      alert('Invalid file type. Please select a PDF, DOCX, or DOC file.')
      return
    }
    setAttachmentLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    dispatch(
      aICreatorUploadAttachmentAction(formData, file.name, () => {
        setAttachmentLoading(false)
      }),
    )
  }

  const checkAttachmentStatus = async () => {
    if (!isChecking && attachments.length > 0) {
      setIsChecking(true)
      for (const attachment of attachments) {
        if (
          !attachment.succeeded &&
          attachment.upload_status !== 'partial_failure' &&
          attachment.upload_status !== 'failed'
        ) {
          dispatch(
            aICreatorCheckAttachmentStatusAction(
              attachment.attachment_id,
              response => {
                if (
                  !response.succeeded &&
                  response.error_details &&
                  response.error_details.length > 0
                ) {
                  alert(response.error_details.join(', '))
                }
              },
            ),
          )
        }
      }
      setIsChecking(false)
    }
  }

  const getAttachmentTitles = attachmentIds => {
    dispatch(
      aICreatorGetAttachmentTitlesAction(attachmentIds, response => {
        if (response) {
          let allHeaders = []
          response.forEach(doc => {
            if (doc?.attachment?.headers) {
              const flattened = flattenHeaders(
                doc.attachment.headers,
                0,
                '',
                doc.attachment,
              )
              allHeaders.push({
                fileName: doc.attachment.name,
                headers: flattened,
              })
            }
          })
          setFlattenedHeaders(allHeaders)
          if (onFlattenedHeadersChange) {
            onFlattenedHeadersChange(allHeaders)
          }
        }
      }),
    )
  }

  const handleTitleSelect = title => {
    setSelectedTitle(title)
    if (onChange) {
      onChange({
        selectedContent: title,
        attachments: title,
      })
    }
  }

  useEffect(() => {
    const interval = setInterval(checkAttachmentStatus, 3000)
    return () => clearInterval(interval)
  }, [attachments])

  useEffect(() => {
    const processedAttachments = attachments.filter(att => att.succeeded)
    if (processedAttachments.length === 0) {
      // Lost all attachments — drop any stale cached headers.
      if (flattenedHeaders.length > 0) {
        setFlattenedHeaders([])
        if (onFlattenedHeadersChange) onFlattenedHeadersChange([])
      }
      lastFetchedIdsRef.current = ''
      return
    }
    const idsKey = processedAttachments
      .map(att => att.attachment_id)
      .sort()
      .join(',')
    // Refetch titles only when the set of attachment IDs actually changes —
    // covers both the initial-cache-stale case and the parent updating to a
    // different course source PDF.
    if (idsKey !== lastFetchedIdsRef.current) {
      lastFetchedIdsRef.current = idsKey
      getAttachmentTitles(processedAttachments.map(att => att.attachment_id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments])

  const userControl = UserType[window.CONSTANTS.USER_ROLE_ID]

  const isUserAllowed =
    userControl == 'Basic' ||
    userControl == 'AppSumo (Tier 2)' ||
    userControl == 'AppSumo (Tier 1)' ||
    userControl == 'AppSumo (Tier 2A)' ||
    userControl == 'AppSumo (Tier 1A)'

  return !isUserAllowed ? (
    <div className={classes.container}>
      {!isPage && (
        <div className={classes.uploadSection}>
          <AttachmentUploader
            attachments={attachments}
            fileInputRef={fileInputRef}
            onUploadFile={handleUploadFile}
            loading={attachmentLoading}
            isPage={isPage}
          />
        </div>
      )}

      {isPage && flattenedHeaders.length === 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '24px 0',
          }}
        >
          <CircularProgress size={24} />
        </div>
      )}

      {flattenedHeaders.length > 0 && (
        <ContentSelector
          flattenedHeaders={flattenedHeaders}
          selectedTitle={selectedTitle}
          onTitleSelect={handleTitleSelect}
        />
      )}
    </div>
  ) : (
    <ResourceBanner />
  )
}

const flattenHeaders = (
  headers,
  level = 0,
  parentTitle = '',
  attachmentData = null,
) => {
  let flattened = []
  headers.forEach(header => {
    const indent = '\u00A0'.repeat(level * 4)
    const title = header.element_id
    flattened.push({
      ...header,
      level,
      displayTitle: indent + title,
      fullPath: parentTitle ? `${parentTitle} > ${title}` : title,
      attachment_id: attachmentData?.id,
      url: attachmentData?.url,
      fileName: attachmentData?.name,
    })
    if (header.children && header.children.length > 0) {
      flattened = flattened.concat(
        flattenHeaders(header.children, level + 1, title, attachmentData),
      )
    }
  })
  return flattened
}

export default withStyles(styles)(PdfView)
